import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { getMetricsBatch } from '@/features/metrics/data';
import { queryKeys } from '@/shared/hooks';
import type { SuiteListItemDto } from '@flowqa/shared';

type TableMetricSeries = {
  dates: Date[];
  passRate: Array<number | null>;
  frequencyPerDay: Array<number | null>;
  flakeScore: Array<number | null>;
  averageDurationMs: Array<number | null>;
};

type UseSuitesTableMetricsModelParams = {
  enableMetricsBatch: boolean;
  isAuthenticated: boolean;
  metricSeriesBySuiteIdOverride?: Map<string, TableMetricSeries>;
  metricsBatchDelayMs: number;
  metricsEndDay?: string | null;
  metricsStartDay?: string | null;
  selectedOrganisationId: string | null;
  sparklineRangeDays: 7 | 14 | 30;
  visibleRows: SuiteListItemDto[];
};

type UseSuitesTableMetricsModelResult = {
  metricSeriesBySuiteId: Map<string, TableMetricSeries>;
};

export const useSuitesTableMetricsModel = ({
  enableMetricsBatch,
  isAuthenticated,
  metricSeriesBySuiteIdOverride,
  metricsBatchDelayMs,
  metricsEndDay,
  metricsStartDay,
  selectedOrganisationId,
  sparklineRangeDays,
  visibleRows,
}: UseSuitesTableMetricsModelParams): UseSuitesTableMetricsModelResult => {
  const metricsRange = sparklineRangeDays === 14 ? '14d' : sparklineRangeDays === 30 ? '30d' : '7d';
  const metricsRangeKey =
    metricsStartDay && metricsEndDay ? `${metricsStartDay}_${metricsEndDay}` : 'all';
  const shouldQueryPerRowMetrics =
    enableMetricsBatch && isAuthenticated && metricSeriesBySuiteIdOverride === undefined;
  const [metricsBatchDelayElapsed, setMetricsBatchDelayElapsed] = useState(
    metricsBatchDelayMs <= 0,
  );
  const metricsEntityKey = useMemo(
    () => visibleRows.map((row) => `${row.projectId}:${row.id}`).join(','),
    [visibleRows],
  );

  useEffect(() => {
    if (metricsBatchDelayMs <= 0) {
      setMetricsBatchDelayElapsed(true);
      return;
    }
    if (visibleRows.length === 0) {
      setMetricsBatchDelayElapsed(false);
      return;
    }
    setMetricsBatchDelayElapsed(false);
    const timeoutId = window.setTimeout(() => {
      setMetricsBatchDelayElapsed(true);
    }, metricsBatchDelayMs);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [metricsBatchDelayMs, visibleRows.length]);

  const metricsBatchQuery = useQuery({
    queryKey: queryKeys.metricsBatch(
      selectedOrganisationId ?? null,
      'suite',
      metricsRange,
      metricsEntityKey,
      metricsRangeKey,
    ),
    enabled: shouldQueryPerRowMetrics && visibleRows.length > 0 && metricsBatchDelayElapsed,
    staleTime: 30_000,
    queryFn: async () =>
      await getMetricsBatch(
        {
          range: metricsRange,
          scope: 'suite',
          startDay: metricsStartDay ?? null,
          endDay: metricsEndDay ?? null,
          entities: visibleRows.map((row) => ({ entityId: row.id, projectId: row.projectId })),
        },
        selectedOrganisationId ?? undefined,
      ),
  });

  const metricSeriesBySuiteId = useMemo(() => {
    const metricsBySuiteId = new Map(
      (metricsBatchQuery.data ?? []).map((item) => [item.entityId, item.metrics]),
    );
    const result = new Map<string, TableMetricSeries>();
    visibleRows.forEach((row) => {
      const queryData = metricsBySuiteId.get(row.id);
      if (!queryData) return;
      const dates = queryData.metricsRangeDates.map((day) => dayjs(day).toDate());
      const passRate = queryData.series.filteredPassRate.map((point) => point.value);
      const frequencyPerDay = queryData.series.executionTrendMetrics.map((point) => point.value);
      const flakeScore = queryData.series.flakeScoreTrend.map((point) => point.value);
      const averageDurationMs = queryData.series.executionDurationAverage.map((point) =>
        point.value === null ? null : point.value * 1000,
      );
      if (
        dates.length === 0 ||
        passRate.length !== dates.length ||
        frequencyPerDay.length !== dates.length ||
        flakeScore.length !== dates.length ||
        averageDurationMs.length !== dates.length
      ) {
        return;
      }
      result.set(row.id, { dates, passRate, frequencyPerDay, flakeScore, averageDurationMs });
    });
    return result;
  }, [metricsBatchQuery.data, visibleRows]);

  return { metricSeriesBySuiteId: metricSeriesBySuiteIdOverride ?? metricSeriesBySuiteId };
};
